import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { fetchPostsList, fetchDeletePostSubmission } from 'modules/posts/actions';
import PostsComponent from 'modules/posts/components/PostsComponent';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const PostsContainer = ({ postsListApiCallBody }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const isFetchingMorePosts = useRef(false);

  const { postsList } = useSelector(state => ({
    postsList: state.posts.postsList,
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const companyProfileKey = sessionStorage.getItem('companyProfileKeyAuthCookie');
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [showCopiedTextOverlay, setShowCopiedTextOverlay] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPreviewFilePopupOpen, setIsPreviewFilePopupOpen] = useState(false);
  const [previewFileData, setPreviewFileData] = useState({});
  const [isActionPopupOpen, setIsActionPopupOpen] = useState(false);
  const [isActionPopupLoading, setIsActionPopupLoading] = useState(false);
  const [actionPopupTarget, setActionPopupTarget] = useState('');
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  const isBottomReached = element => {
    if (!element) return false;
    return element.getBoundingClientRect().bottom - window.innerHeight < 20;
  };

  const scrollToShowMorePosts = () => {
    const wrappedElement = document.getElementById('posts-wrapper');
    const isFetchingMorePostsValue = isFetchingMorePosts.current;

    if (
      isBottomReached(wrappedElement) &&
      postsList?.details?.nextQuery &&
      isFetchingMorePostsValue === false
    ) {
      isFetchingMorePosts.current = true;

      const body = {
        profileKey: companyProfileKey,
        nextQuery: postsList.details.nextQuery,
        queryData: postsListApiCallBody.current,
        callbackAction() {
          isFetchingMorePosts.current = false;
        },
      };
      dispatch(fetchPostsList(body));
    }
  };

  useEffect(
    () => () => {
      document.removeEventListener('scroll', scrollToShowMorePosts);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    document.addEventListener('scroll', scrollToShowMorePosts);
    return () => {
      document.removeEventListener('scroll', scrollToShowMorePosts);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postsList]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // eslint-disable-next-line no-unused-vars
  const submitSearchQuery = reset => {
    // setSelectedPageFilter(1);
    // const body = {
    //   ...ordersApiCallParams,
    // };
    // if (searchQuery) body.searchTerm = searchQuery;
    // if (reset) delete body.searchTerm;
    // setOrdersApiCallParams(body);
    // dispatch(fetchAllOrders(body));
  };

  const reloadPostsPage = () => {
    const body = {
      profileKey: companyProfileKey,
      queryData: postsListApiCallBody.current,
      override: true,
    };

    dispatch(fetchPostsList(body));
  };

  const actionPopupConfirmBtnCallback = shouldReloadOrderDetails => {
    setIsActionPopupOpen(false);
    setIsActionPopupLoading(false);
    setActionPopupTarget('');

    if (shouldReloadOrderDetails) reloadPostsPage();
  };
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleTextToCopyToClipboardClick = textToCopy => {
    navigator.clipboard.writeText(textToCopy);
    setShowCopiedTextOverlay(true);

    setTimeout(() => {
      setShowCopiedTextOverlay(false);
    }, 1000);
  };

  const handleAddPostBtnClick = () => {
    navigate('/addPost');
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;
    const searchQueryMinLength = 3;

    if (value.length > 30) return;
    setSearchQuery(value);

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);

        submitSearchQuery();
      }, 300);
    }
    // this condition checks if we are deleteing letters from search query and we reached
    // 2 characters, then we need to clear search query filter
    else if (searchQuery.length >= searchQueryMinLength && value.length < searchQueryMinLength)
      submitSearchQuery(true);
  };

  const handlePreviewFileClick = (openPopup, fileType, fileUrl) => {
    if (openPopup) {
      setPreviewFileData({ type: fileType, url: fileUrl });
      setIsPreviewFilePopupOpen(true);
    } else setIsPreviewFilePopupOpen(false);
  };

  const handleActionBtnClick = postKey => {
    setIsActionPopupOpen(true);
    setActionPopupTarget(postKey);
  };

  const handleActionPopupConfirmBtnClick = () => {
    setIsActionPopupLoading(true);

    const body = {
      postKey: actionPopupTarget,
      actionPopupCallbackAction: actionPopupConfirmBtnCallback,
    };

    dispatch(fetchDeletePostSubmission(body));
  };

  const handleActionPopupDismissBtnClick = () => {
    setIsActionPopupOpen(false);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <PostsComponent
      isFetchingMorePosts={isFetchingMorePosts.current}
      showCopiedTextOverlay={showCopiedTextOverlay}
      searchQuery={searchQuery}
      isPreviewFilePopupOpen={isPreviewFilePopupOpen}
      previewFileData={previewFileData}
      isActionPopupOpen={isActionPopupOpen}
      isActionPopupLoading={isActionPopupLoading}
      handleTextToCopyToClipboardClick={handleTextToCopyToClipboardClick}
      handleAddPostBtnClick={handleAddPostBtnClick}
      handleSearchBarInputChange={handleSearchBarInputChange}
      handlePreviewFileClick={handlePreviewFileClick}
      handleActionBtnClick={handleActionBtnClick}
      handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
      handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
    />
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

PostsContainer.propTypes = {
  postsListApiCallBody: PropTypes.shape({
    current: PropTypes.shape({}),
  }).isRequired,
};

PostsContainer.defaultProps = {};

export default PostsContainer;
